import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Car, CalendarDays, MapPin, Gift, Copy, Check } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AddToCalendar from "@/components/AddToCalendar";

export default function PaymentSuccess() {
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const isBalance = search.get("kind") === "balance";
  const [status, setStatus] = useState("checking");
  const [booking, setBooking] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let a = 0;
    const poll = async () => {
      if (cancelled) return;
      a += 1;
      setAttempts(a);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          const bid = data.booking_id;
          if (bid) {
            const { data: b } = await api.get(`/bookings/${bid}/public`);
            setBooking(b);
          }
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
        if (a >= 15) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const referralLink = !isBalance && booking?.referral_code_issued
    ? `${window.location.origin}/book?ref=${booking.referral_code_issued}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.error("Clipboard copy failed", e);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f17]">
      <Navbar />
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16" data-testid="payment-success-section">
        <div className="cj-card p-8 sm:p-12 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="h-14 w-14 text-[#ff6b00] mx-auto animate-spin" />
              <h1 className="font-display mt-6 text-3xl sm:text-4xl font-extrabold uppercase text-slate-100">
                {isBalance ? "Confirming your payment…" : "Confirming your deposit…"}
              </h1>
              <p className="mt-3 text-slate-400 font-mono text-sm">Checking Stripe (attempt {attempts}/15)</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="h-16 w-16 mx-auto rounded-2xl bg-[#10b981]/15 flex items-center justify-center cj-pulse">
                <CheckCircle2 className="h-10 w-10 text-[#10b981]" />
              </div>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#10b981] mt-5">{isBalance ? "Payment received" : "Deposit confirmed"}</div>
              <h1 className="font-display mt-2 text-3xl sm:text-5xl font-extrabold uppercase text-slate-100" data-testid="booking-confirmation-heading">
                {isBalance ? "Balance paid in full." : "You're booked in."}
              </h1>
              <p className="mt-3 text-slate-400">
                {isBalance
                  ? "Thanks — your balance is settled. A receipt is on its way to your inbox."
                  : "A branded confirmation has landed in your inbox — see you on the day."}
              </p>

              {!isBalance && booking && (
                <div className="mt-8 text-left rounded-xl border border-white/10 overflow-hidden">
                  <Row icon={Car} label="Vehicle" value={`${booking.vehicle.year} ${booking.vehicle.make} ${booking.vehicle.model}`} />
                  <Row icon={CalendarDays} label="When" value={`${booking.scheduled_date} · ${booking.scheduled_time}`} />
                  <Row icon={MapPin} label="Where" value={booking.customer.address} />
                  <Row label="Service" value={booking.service_name} />
                  {booking.travel_surcharge > 0 && <Row label="Travel fee" value={`+$${booking.travel_surcharge.toFixed(2)}`} />}
                  {booking.loyalty_credit > 0 && <Row label="Loyalty credit" value={`-$${booking.loyalty_credit.toFixed(2)}`} />}
                  {booking.referral_credit > 0 && <Row label="Referral credit" value={`-$${booking.referral_credit.toFixed(2)}`} />}
                  <Row label="Balance on completion" value={`$${booking.final_balance.toFixed(2)}`} />
                  <Row label="Booking ID" mono value={booking.id} testid="booking-confirmation-id" />
                </div>
              )}

              {referralLink && (
                <div className="mt-6 text-left">
                  <AddToCalendar booking={booking} />
                </div>
              )}

              {referralLink && (
                <div
                  className="mt-6 text-left p-5 rounded-xl border border-[#ff6b00]/30 bg-[#ff6b00]/5"
                  data-testid="referral-share-card"
                >
                  <div className="flex items-center gap-2">
                    <Gift className="h-5 w-5 text-[#ff6b00]" />
                    <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#ff6b00]">
                      Refer a friend · you both save $10
                    </div>
                  </div>
                  <div className="mt-3 font-display font-extrabold text-2xl text-slate-100 break-all">
                    {booking.referral_code_issued}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      readOnly
                      value={referralLink}
                      data-testid="referral-link-input"
                      className="flex-1 bg-[#0f1522] border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-300 font-mono truncate"
                    />
                    <button
                      onClick={copyLink}
                      data-testid="referral-copy-button"
                      className="cj-btn-ghost px-4 py-2 rounded-lg text-xs inline-flex items-center gap-1"
                    >
                      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-slate-400">
                    Share the link. When a mate books their first detail with it, they get $10 off and you earn a $10 credit on your next booking.
                  </p>
                </div>
              )}

              <Link to="/" data-testid="payment-success-home-button" className="cj-btn-primary mt-8 inline-flex px-6 py-3 rounded-full">
                Back to Home
              </Link>
            </>
          )}
          {status === "failed" && (
            <>
              <h1 className="font-display text-3xl font-extrabold uppercase text-red-400">Payment issue</h1>
              <p className="mt-3 text-slate-400">{isBalance ? "Your balance payment didn't go through. No charge was made — give it another go." : "Your deposit didn't go through. No charge was made — give it another go."}</p>
              <Link to="/book" className="cj-btn-primary mt-6 inline-flex px-6 py-3 rounded-full">
                Try again
              </Link>
            </>
          )}
          {status === "timeout" && (
            <>
              <h1 className="font-display text-3xl font-extrabold uppercase text-slate-100">Still processing…</h1>
              <p className="mt-3 text-slate-400">
                Stripe is a little slow right now. If you were charged you'll see a confirmation email shortly.
              </p>
              <Link to="/" className="cj-btn-ghost mt-6 inline-flex px-6 py-3 rounded-full">
                Back to home
              </Link>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Row({ icon: Icon, label, value, mono, testid }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-[#0f1522] border-b border-white/5 last:border-b-0">
      {Icon && <Icon className="h-4 w-4 text-slate-500 shrink-0" />}
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 w-28">{label}</div>
      <div data-testid={testid} className={`text-sm text-slate-200 ${mono ? "font-mono text-xs break-all" : ""}`}>
        {value}
      </div>
    </div>
  );
}
